/**
 * Cart Drawer — Horizon-pattern AJAX cart.
 * Shimmer effect on prices during updates.
 */
import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, CartUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CartDrawerComponent extends DialogComponent {
  #updating = false;

  get sectionId() {
    return this.dataset.sectionId;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartAdd, this.#onCartAdd);
    this.#bindCartIconClicks();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartAdd, this.#onCartAdd);
  }

  #bindCartIconClicks() {
    document.querySelectorAll('.header__cart-icon').forEach((el) => {
      el.addEventListener('click', this.#onCartIconClick);
    });
  }

  #onCartIconClick = (event) => {
    event.preventDefault();
    this.showDialog();
  };

  #onCartAdd = (event) => {
    const html = event.detail?.data?.sections?.[this.sectionId];
    if (html) this.#morphContent(html);
    if (!this.refs.dialog?.open) this.showDialog();
  };

  async showDialog() {
    await super.showDialog();
    this.#reinitSliders();
  }

  onDialogClick(event) {
    const rect = this.refs.dialog.getBoundingClientRect();
    const outside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;
    if (outside) this.closeDialog();
  }

  async updateQuantity(event) {
    const btn = event.target.closest('[data-line]');
    if (!btn || this.#updating) return;
    const line = parseInt(btn.dataset.line, 10);
    this.#shimmerLine(line);
    await this.#cartChange(line, Math.max(0, parseInt(btn.dataset.qty, 10)));
  }

  async removeItem(event) {
    const btn = event.target.closest('[data-line]');
    if (!btn || this.#updating) return;
    const line = parseInt(btn.dataset.line, 10);
    this.#shimmerLine(line);
    await this.#cartChange(line, 0);
  }

  async updateQuantitySelect(event) {
    const select = event.target.closest('[data-line]');
    if (!select || this.#updating) return;
    const line = parseInt(select.dataset.line, 10);
    this.#shimmerLine(line);
    await this.#cartChange(line, parseInt(select.value, 10));
  }

  async addFromRec(event) {
    const btn = event.target.closest('[data-variant-id]');
    if (!btn || this.#updating) return;

    btn.disabled = true;
    btn.classList.add('is-loading');
    this.#updating = true;
    this.#shimmerTotal();

    try {
      const body = JSON.stringify({
        items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }],
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.errors) {
        console.error('Cart add error:', data.errors || data.description);
        btn.classList.remove('is-loading');
        btn.disabled = false;
        this.#updating = false;
        return;
      }

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');

      if (data.sections?.[this.sectionId]) {
        this.#morphContent(data.sections[this.sectionId]);
      }

      document.dispatchEvent(new CartAddEvent(data, this.sectionId, {
        sections: data.sections,
      }));

      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.disabled = false;
      }, 2000);
    } catch (err) {
      console.error('Cart add error:', err);
      btn.classList.remove('is-loading');
      btn.disabled = false;
    } finally {
      this.#updating = false;
    }
  }

  async #cartChange(line, quantity) {
    this.#updating = true;

    try {
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const res = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const data = await res.json();

      if (data.errors) {
        console.error('Cart error:', data.errors);
        return;
      }

      if (data.sections?.[this.sectionId]) {
        this.#morphContent(data.sections[this.sectionId]);
      }

      document.dispatchEvent(new CartUpdateEvent(data, this.sectionId, {
        itemCount: data.item_count,
        sections: data.sections,
      }));
    } catch (err) {
      console.error('Cart change error:', err);
    } finally {
      this.#updating = false;
    }
  }

  /**
   * Add shimmer effect on a specific line price + the total
   */
  #shimmerLine(line) {
    const linePrice = this.querySelector(`[data-line-price="${line}"]`);
    if (linePrice) linePrice.classList.add('shimmer-text');
    this.#shimmerTotal();
  }

  #shimmerTotal() {
    const total = this.querySelector('[data-cart-total]');
    if (total) total.classList.add('shimmer-text');
  }

  #morphContent(sectionHTML) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHTML, 'text/html');

    const newDialog = doc.querySelector('.cart-drawer');
    const currentDialog = this.querySelector('.cart-drawer');

    if (!newDialog || !currentDialog) return;

    currentDialog.innerHTML = newDialog.innerHTML;
    this.#reinitSliders();
  }

  #reinitSliders() {
    if (typeof window.initCvSliders !== 'function') return;
    this.querySelectorAll('.js-cv-slider.is-initialized').forEach((s) =>
      s.classList.remove('is-initialized')
    );
    requestAnimationFrame(() => window.initCvSliders());
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}